import type { Problem } from "@ppx/shared-types";
import { ApiError, request } from "./api";
import type {
  AssignInput,
  Category,
  CategoryInput,
  CloseInput,
  Reply,
  ReplyInput,
  ReopenInput,
  ResolveInput,
  SlaPolicy,
  SlaPolicyInput,
  SupportPage,
  SupportStats,
  Ticket,
  TicketDetail,
  TicketInput,
  TicketListParams,
  TicketUpdateInput,
} from "./support-types";

type QueryValue = string | number | boolean | undefined;

function qs(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) {
      continue;
    }
    search.set(key, String(value));
  }
  const serialized = search.toString();
  return serialized === "" ? "" : `?${serialized}`;
}

export function fetchSupportStats(): Promise<SupportStats> {
  return request<SupportStats>("/support/stats");
}

export function listTickets(params: TicketListParams = {}): Promise<SupportPage<Ticket>> {
  return request<SupportPage<Ticket>>(
    `/support/tickets${qs({
      q: params.q,
      status: params.status,
      priority: params.priority,
      assignee_user_id: params.assignee_user_id,
      category_id: params.category_id,
      channel: params.channel,
      linked_deal_id: params.linked_deal_id,
      include_deleted: params.include_deleted ? "true" : undefined,
      cursor: params.cursor,
      limit: params.limit,
    })}`,
  );
}

export function getTicket(id: string): Promise<TicketDetail> {
  return request<TicketDetail>(`/support/tickets/${encodeURIComponent(id)}`);
}

export function createTicket(input: TicketInput): Promise<Ticket> {
  return request<Ticket>("/support/tickets", { method: "POST", body: input });
}

export function updateTicket(id: string, input: TicketUpdateInput): Promise<Ticket> {
  return request<Ticket>(`/support/tickets/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: input,
  });
}

export function deleteTicket(id: string): Promise<{ id: string; deleted: boolean }> {
  return request<{ id: string; deleted: boolean }>(`/support/tickets/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function addReply(id: string, input: ReplyInput): Promise<Reply> {
  return request<Reply>(`/support/tickets/${encodeURIComponent(id)}/replies`, {
    method: "POST",
    body: input,
  });
}

export function assignTicket(id: string, input: AssignInput): Promise<Ticket> {
  return request<Ticket>(`/support/tickets/${encodeURIComponent(id)}/assign`, {
    method: "POST",
    body: input,
  });
}

export function resolveTicket(id: string, input: ResolveInput): Promise<Ticket> {
  return request<Ticket>(`/support/tickets/${encodeURIComponent(id)}/resolve`, {
    method: "POST",
    body: input,
  });
}

export function closeTicket(id: string, input: CloseInput): Promise<Ticket> {
  return request<Ticket>(`/support/tickets/${encodeURIComponent(id)}/close`, {
    method: "POST",
    body: input,
  });
}

export function reopenTicket(id: string, input: ReopenInput): Promise<Ticket> {
  return request<Ticket>(`/support/tickets/${encodeURIComponent(id)}/reopen`, {
    method: "POST",
    body: input,
  });
}

export function listCategories(): Promise<SupportPage<Category>> {
  return request<SupportPage<Category>>("/support/categories");
}

export function createCategory(input: CategoryInput): Promise<Category> {
  return request<Category>("/support/categories", { method: "POST", body: input });
}

export function updateCategory(id: string, input: Partial<CategoryInput>): Promise<Category> {
  return request<Category>(`/support/categories/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: input,
  });
}

export function deleteCategory(id: string): Promise<{ id: string; deleted: boolean }> {
  return request<{ id: string; deleted: boolean }>(
    `/support/categories/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

export function listSlaPolicies(): Promise<SupportPage<SlaPolicy>> {
  return request<SupportPage<SlaPolicy>>("/support/sla-policies");
}

export function createSlaPolicy(input: SlaPolicyInput): Promise<SlaPolicy> {
  return request<SlaPolicy>("/support/sla-policies", { method: "POST", body: input });
}

export function updateSlaPolicy(
  id: string,
  input: Partial<SlaPolicyInput>,
): Promise<SlaPolicy> {
  return request<SlaPolicy>(`/support/sla-policies/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: input,
  });
}

export function deleteSlaPolicy(id: string): Promise<{ id: string; deleted: boolean }> {
  return request<{ id: string; deleted: boolean }>(
    `/support/sla-policies/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function supportDuplicateCandidateId(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.code !== "support_duplicate") {
    return null;
  }
  const problem = error.problem as (Problem & { dedupe_candidate_id?: unknown }) | null;
  const value = problem?.dedupe_candidate_id;
  return typeof value === "string" && value !== "" ? value : null;
}

export function supportErrorCode(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}